import theme, { defaultFullscreenPageStyling } from "@/theme";
import { Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { convertPxToRem } from "@/utils";
import {
  MembershipDependent,
  PathologyPanel,
  PathologyPanelChargeCode,
} from "@/services/core-api-adapter";
import { PATHOLOGY_PANEL_PILL_MAP_V2 } from "@/features/Common/constants";
import {
  trackEvent,
  trackPageViewV2,
  trackUserInteraction,
} from "@/services/analytics-adapter";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import ItemPill from "@/features/Common/components/ItemPill";
import IconLoader from "@/features/Common/components/IconLoader";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import { getConfigurationVariable } from "@/services/configuration-adapter";
import InfoContextPanel from "@/features/Common/components/InfoContextPanel";
import { useEffect } from "react";

const PATHOLOGY_FACILITY_LINK =
  getConfigurationVariable("VITE_APP_UNU_HEALTH_PATHOLOGY_FACILITY_LINK") ||
  "https://www.ampath.co.za/labs";

interface PathologyPanelsProps {
  panelList: PathologyPanel[];
  consultAmountFormatted: string;
  onPanelClick: (panel: PathologyPanel) => void;
  onStartDoctorConsultationClick: () => void;
  isDiscountApplied: boolean;
  onBackButtonClick?: () => void;
  consultOriginalAmountFormatted: string;
  selectedDependant?: MembershipDependent | null;
  isVoucherSelected?: boolean;
}

export default function PathologyPanels({
  panelList,
  consultAmountFormatted,
  onPanelClick,
  onStartDoctorConsultationClick,
  isDiscountApplied,
  onBackButtonClick,
  consultOriginalAmountFormatted,
  selectedDependant,
  isVoucherSelected = false,
}: PathologyPanelsProps) {
  const { t } = useTranslation();

  function onClick(panel: PathologyPanel) {
    trackEvent({
      event: "action.pathologyPanelSelected",
      source: "Pathology panel list",
      panelName: panel.panelName,
    });

    trackUserInteraction({
      linkText: `Pathology Panels | ${panel.panelName}`,
      linkIntent: "navigational",
      linkScope: "button",
    });

    onPanelClick(panel);
  }

  function onFindAFacilityClick() {
    trackEvent({
      event: "action.pathologyPanelsFindAFacilityClicked",
      source: "Pathology panels",
    });
  }

  function getChronicConditionsPanels(): PathologyPanel[] {
    const chronicConditionsPanels = [
      PathologyPanelChargeCode.ANAEMIA,
      PathologyPanelChargeCode.HBA1C,
      PathologyPanelChargeCode.DIABETES_MONITORING,
      PathologyPanelChargeCode.CHOLESTEROL_MONITORING,
      PathologyPanelChargeCode.BLOOD_PRESSURE_MONITORING,
    ];

    const panels =
      panelList.filter((panel) => {
        if (chronicConditionsPanels.includes(panel.panelChargeCode)) {
          return panel;
        }
      }) || [];
    return panels as PathologyPanel[];
  }

  function getSexualHealthPanels(): PathologyPanel[] {
    const sexualHealthPanels = [
      PathologyPanelChargeCode.STD_PCR,
      PathologyPanelChargeCode.STD_SCREENING_INCL_HIV,
      PathologyPanelChargeCode.STD_SCREENING_EXCL_HIV,
      PathologyPanelChargeCode.SYPHILIS,
      PathologyPanelChargeCode.HEPATITIS_B,
    ];

    const panels =
      panelList.filter((panel) => {
        if (sexualHealthPanels.includes(panel.panelChargeCode)) {
          return panel;
        }
      }) || [];

    return panels as PathologyPanel[];
  }

  function getPregnancyPanels(): PathologyPanel[] {
    const pregnancyPanels = [PathologyPanelChargeCode.ANTENATAL];

    const panels =
      panelList.filter((panel) => {
        if (pregnancyPanels.includes(panel.panelChargeCode)) {
          return panel;
        }
      }) || [];

    return panels as PathologyPanel[];
  }

  function getHIVPanels(): PathologyPanel[] {
    const hivScreeningAndMonitoringPanels = [
      PathologyPanelChargeCode.HIV_MONITORING,
      PathologyPanelChargeCode.CD4_VIRAL_LOAD,
      PathologyPanelChargeCode.HIV_SCREENING,
    ];

    const panels =
      panelList.filter((panel) => {
        if (hivScreeningAndMonitoringPanels.includes(panel.panelChargeCode)) {
          return panel;
        }
      }) || [];

    return panels as PathologyPanel[];
  }

  function getGeneralConditionsPanels(): PathologyPanel[] {
    const generalConditionsPanels = [
      PathologyPanelChargeCode.FEMALE,
      PathologyPanelChargeCode.MALE_UNDER_45,
      PathologyPanelChargeCode.MALE_OVER_45,
    ];

    const panels =
      panelList.filter((panel) => {
        if (generalConditionsPanels.includes(panel.panelChargeCode)) {
          return panel;
        }
      }) || [];

    return panels as PathologyPanel[];
  }

  useEffect(() => {
    trackPageViewV2({
      pageName: "Pathology:PathologyPanels",
      pageSubSection1: "Pathology",
      pageSubSection2: "Pathology:PathologyPanels",
      pageCategory: "Pathology",
    });
  }, []);

  return (
    <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
      {selectedDependant && (
        <InfoContextPanel
          title={t("SelectedDependantPanel.title")}
          text={selectedDependant.firstName + " " + selectedDependant.lastName}
        />
      )}
      {isVoucherSelected && (
        <InfoContextPanel
          title={t("InfoContextPanel.vouchers.title")}
          icon="CloudStatusIcon"
          text={t(`InfoContextPanel.vouchers.BloodTestVoucher.subtitle`)}
        />
      )}
      <Stack spacing={2}>
        <Typography variant="h2">{t("PathologyPanel.page.title")}</Typography>
        <Typography variant="h3">
          {t("PathologyPanel.page.subtitle")}
        </Typography>
        <ButtonWithAnalytics
          page={`PathologyPanels`}
          text={t("PathologyPanelDetails.findAFacility")}
          intent="confirmational"
          component="a"
          role="link"
          target="_blank"
          onClick={onFindAFacilityClick}
          href={PATHOLOGY_FACILITY_LINK}
          variant="outlined"
          size="small"
        >
          {t("PathologyPanelDetails.findAFacility")}
        </ButtonWithAnalytics>
      </Stack>
      <Stack spacing={2}>
        {panelList && (
          <Stack spacing={2}>
            <Typography>
              {t("PathologyPanel.category.chronicConditions.title")}
            </Typography>
            {getChronicConditionsPanels().map((panel, index) => (
              <ItemPill
                key={panel.panelName + index}
                onClick={() => {
                  onClick(panel);
                }}
                selectedItem={panel}
                shouldShowSubtitle={true}
                itemMap={PATHOLOGY_PANEL_PILL_MAP_V2}
                titleStyles={{
                  fontSize: convertPxToRem(16),
                  fontWeight: 600,
                }}
                shouldShowPrice
                shouldShowDiscount={Boolean(
                  panel.isDiscountApplied && panel.discountAmountFormatted
                )}
              />
            ))}
            <Typography>
              {t("PathologyPanel.category.sexualHealth.title")}
            </Typography>
            {getSexualHealthPanels().map((panel, index) => (
              <ItemPill
                key={panel.panelName + index}
                onClick={() => {
                  onClick(panel);
                }}
                selectedItem={panel}
                shouldShowSubtitle={true}
                itemMap={PATHOLOGY_PANEL_PILL_MAP_V2}
                titleStyles={{
                  fontSize: convertPxToRem(16),
                  fontWeight: 600,
                }}
                shouldShowPrice
                shouldShowDiscount={Boolean(
                  panel.isDiscountApplied && panel.discountAmountFormatted
                )}
              />
            ))}
            <Typography>
              {t("PathologyPanel.category.pregnancy.title")}
            </Typography>
            {getPregnancyPanels().map((panel, index) => (
              <ItemPill
                key={panel.panelName + index}
                onClick={() => {
                  onClick(panel);
                }}
                selectedItem={panel}
                shouldShowSubtitle={true}
                itemMap={PATHOLOGY_PANEL_PILL_MAP_V2}
                titleStyles={{
                  fontSize: convertPxToRem(16),
                  fontWeight: 600,
                }}
                shouldShowPrice
                shouldShowDiscount={Boolean(
                  panel.isDiscountApplied && panel.discountAmountFormatted
                )}
              />
            ))}
            <Typography>{t("PathologyPanel.category.hiv.title")}</Typography>
            {getHIVPanels().map((panel, index) => (
              <ItemPill
                key={panel.panelName + index}
                onClick={() => {
                  onClick(panel);
                }}
                selectedItem={panel}
                shouldShowSubtitle={true}
                itemMap={PATHOLOGY_PANEL_PILL_MAP_V2}
                titleStyles={{
                  fontSize: convertPxToRem(16),
                  fontWeight: 600,
                }}
                shouldShowPrice
                shouldShowDiscount={Boolean(
                  panel.isDiscountApplied && panel.discountAmountFormatted
                )}
              />
            ))}
            <Typography>
              {t("PathologyPanel.category.generalConditions.title")}
            </Typography>
            {getGeneralConditionsPanels().map((panel, index) => (
              <ItemPill
                key={panel.panelName + index}
                onClick={() => {
                  onClick(panel);
                }}
                selectedItem={panel}
                shouldShowSubtitle={true}
                itemMap={PATHOLOGY_PANEL_PILL_MAP_V2}
                titleStyles={{
                  fontSize: convertPxToRem(16),
                  fontWeight: 600,
                }}
                shouldShowPrice
                shouldShowDiscount={Boolean(
                  panel.isDiscountApplied && panel.discountAmountFormatted
                )}
              />
            ))}
          </Stack>
        )}

        <Stack
          spacing={2}
          sx={{
            backgroundColor: theme.palette.primary[100],
            borderRadius: 2,
            padding: 2,
          }}
        >
          <Stack flexDirection="row" gap={1}>
            <IconLoader icon="ChatBubblePlusIcon" />
            <Stack>
              <Typography variant="h4">
                {t("PathologyPanel.page.doctorConsultCTA.title")}
              </Typography>
              <Typography variant="h4" fontWeight="400">
                {isDiscountApplied ? (
                  <Trans>
                    {t(
                      "PathologyPanel.page.doctorConsultCTA.discount.subtitle",
                      {
                        amountFormatted: consultAmountFormatted,
                        originalAmountFormatted: consultOriginalAmountFormatted,
                      }
                    )}
                  </Trans>
                ) : (
                  <Trans>
                    {t("PathologyPanel.page.doctorConsultCTA.subtitle", {
                      amountFormatted: consultAmountFormatted,
                    })}
                  </Trans>
                )}
              </Typography>
            </Stack>
          </Stack>
          <ButtonWithAnalytics
            page="PathologyPanel"
            text={t("PathologyPanel.page.doctorConsultCTA.button")}
            intent="navigational"
            variant="outlined"
            color="neutral"
            onClick={onStartDoctorConsultationClick}
          >
            {t("PathologyPanel.page.doctorConsultCTA.button")}
          </ButtonWithAnalytics>
        </Stack>
      </Stack>
    </Stack>
  );
}
